import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, Store, Tag } from 'lucide-react';
import { Announcement } from '../../types';
import { fetchAnnouncements } from '../../services/announcements.service';

const typeConfig = {
  info:    { bg: 'bg-ndms-navy',   text: 'text-white', icon: Info,          accent: 'bg-white/10' },
  warning: { bg: 'bg-amber-500',   text: 'text-white', icon: AlertTriangle,  accent: 'bg-white/10' },
  closed:  { bg: 'bg-red-700',     text: 'text-white', icon: Store,          accent: 'bg-white/10' },
  sale:    { bg: 'bg-ndms-teal',   text: 'text-white', icon: Tag,            accent: 'bg-white/10' },
};

const LS_KEY = 'ndms_announcement';

export function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState<Announcement[]>(() => {
    try {
      const cached = localStorage.getItem(LS_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  useEffect(() => {
    fetchAnnouncements(true).then((data) => {
      setAnnouncements(data);
      try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch {}
    }).catch(console.error);
  }, []);

  if (announcements.length === 0) return null;

  const ann = announcements[0];
  const config = typeConfig[ann.type];
  const Icon = config.icon;
  const hasLines = ann.lines && ann.lines.length > 0;

  return (
    <AnimatePresence>
      <motion.div
        key={ann.announcementId}
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className={`${config.bg} ${config.text} relative z-30 overflow-hidden`}
      >
        <div className="container-max section-padding py-4">
          {/* Main row */}
          <div className="flex items-center gap-4">
            <div className={`${config.accent} rounded-xl p-2.5 flex-shrink-0`}>
              <Icon size={24} />
            </div>

            <div>
              {ann.title && (
                <p className="font-bold text-lg leading-tight">{ann.title}</p>
              )}
              <p className="text-base text-white/90">{ann.message}</p>
            </div>
          </div>

          {/* Schedule lines — always visible */}
          {hasLines && (
            <div className={`${config.accent} rounded-xl px-4 py-3 mt-3`}>
              <div className="grid gap-2">
                {ann.lines!.map((line, i) => {
                  const parts = line.split(/\s[–-]\s/);
                  return (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-white/85 font-medium text-base">
                        {parts[0]}
                      </span>
                      {parts[1] && (
                        <span
                          className={`font-bold text-sm px-3 py-0.5 rounded-full ${
                            parts[1].toUpperCase().includes('CLOSED')
                              ? 'bg-red-500/30 text-red-100'
                              : 'bg-white/20 text-white'
                          }`}
                        >
                          {parts[1]}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
